import { Portal, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as BlankTemplateIcon } from 'assets/icons/blank_page.svg';
import { ReactComponent as BackIcon } from 'assets/icons/chevron_left.svg';
import { ReactComponent as AccessibilityCheckIcon } from 'assets/icons/toolbar/file/AccessibilityCheck.svg';
import { ReactComponent as AccessibleIcon } from 'assets/icons/toolbar/file/Acessible.svg';
import { ReactComponent as LanguageIcon } from 'assets/icons/toolbar/file/Language.svg';
import { ReactComponent as OrderIcon } from 'assets/icons/toolbar/file/Order.svg';
import { ReactComponent as TemplatesIcon } from 'assets/icons/widgetmenu_templates.svg';
import { MY_DESIGN_REDIRECT_LINK, TEMPLATES_REDIRECT_LINK } from 'constants/links';
import {
  ACCESSIBILITY_CHECKER_OPENED,
  COPY_FROM_EDITOR,
  CREATE_FROM_BLANK,
  CREATE_NEW_DESIGN,
  DOCUMENT_LANGUAGE_MODAL_OPENED,
  FILE_MENU,
  READING_ORDER_TAB_OPENED,
  SAVE_AS_COPY_FROM_EDITOR,
  VISUAL_SIMULATOR_TAB_OPENED,
} from 'constants/mixpanel';
import { PAGE_GUIDE } from 'constants/mixpanel';
import { useCreateE2Design } from 'hooks/useCreateE2Design/useCreateE2Design';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { selectInfographTitle } from 'modules/Editor/store/infographSelector';
import { ColorVisualIcon } from 'modules/common/components/ColorVisualIcon';
import type { MenuType } from 'modules/common/components/Menu';
import { ToolbarMenu } from 'modules/common/components/Menu/ToolbarMenu';
import { useToast } from 'modules/common/components/Toast';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AccessibilityViewIndex } from 'types/accessibilityViewIndex';

import { RootState, store, useAppDispatch, useAppSelector } from '../../store';
import { selectHasShowGrid, selectHasShowMargin } from '../../store/editorSettingsSelector';
import { setAccessibilityViewIndex, toggleShowGrid, toggleShowMargin } from '../../store/editorSettingsSlice';
import { useFocus } from '../Focus';
import { BLANK_TEMPLATE_ID } from './FileMenu.config';
import { SetLanguageModal } from './components/SetLanguageModal';

/**
 * File menu.
 */
export const FileMenu = memo(() => {
  const createE2Design = useCreateE2Design();
  const { t } = useTranslation('common_navbar', { useSuspense: false, keyPrefix: 'fileMenu' });
  const { fileMenuRef } = useFocus();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { id: e1UUid } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const hasShowGrid = useAppSelector(selectHasShowGrid);
  const hasShowMargin = useAppSelector(selectHasShowMargin);

  const onShowGrid = () => {
    dispatch(toggleShowGrid());

    Mixpanel.track(PAGE_GUIDE.eventName, { is_enabled: !hasShowGrid, type: PAGE_GUIDE.type.Grid });
  };
  const onShowMargins = () => {
    dispatch(toggleShowMargin());

    Mixpanel.track(PAGE_GUIDE.eventName, { is_enabled: !hasShowMargin, type: PAGE_GUIDE.type.Margin });
  };

  const onCopyDesign = async () => {
    try {
      if (!e1UUid) {
        throw new Error('E1 UUID is not defined');
      }

      const storeState = store.getState() as RootState;
      const infographTitle = selectInfographTitle(storeState);

      const createE2RequestParams = {
        sourceId: e1UUid,
        sourceType: 'e2-infographic' as const,
        mpEvent: COPY_FROM_EDITOR.eventName,

        // Design should appear at top of My Designs
        copyToSide: false,

        // Design will appear in root folder
        folder: '',

        // Don't copy modified_at fields
        fullData: false,
        copyTitle: infographTitle,
      };

      const newDesignId = await createE2Design({
        createE2RequestParams,
        loadingToastString: t('makeACopy.loadingToast'),
        successToastString: t('makeACopy.successToast', { title: infographTitle }),
        errorToastString: t('makeACopy.failToast'),
      });

      if (newDesignId) {
        Mixpanel.track(SAVE_AS_COPY_FROM_EDITOR.eventName, {
          title: infographTitle,
          multipage: true,
        });
        window.open(`/design/${newDesignId}`, '_blank');
      }
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: t('makeACopy.failToast'),
      });
    }
  };

  const onBackToMyDesigns = () => window.open(MY_DESIGN_REDIRECT_LINK, '_self');
  const onCreateFromTemplate = () => {
    Mixpanel.track(CREATE_NEW_DESIGN.eventName, { from: CREATE_NEW_DESIGN.from.Template });
    window.open(TEMPLATES_REDIRECT_LINK, '_blank');
  };
  const onCreateFromBlank = async () => {
    try {
      const createE2RequestParams = {
        sourceId: BLANK_TEMPLATE_ID,
        sourceType: 'e2-template' as const,

        mpEvent: CREATE_FROM_BLANK.eventName,

        // Design will appear in root folder
        folder: '',
      };
      const newInfographId = await createE2Design({
        createE2RequestParams,
        loadingToastString: t('createNew.fromBlank.loadingToast'),
        successToastString: t('createNew.fromBlank.successToast'),
        errorToastString: t('createNew.fromBlank.failToast'),
      });

      if (newInfographId) {
        Mixpanel.track(CREATE_NEW_DESIGN.eventName, { from: CREATE_NEW_DESIGN.from.Blank });
        window.open(`/design/${newInfographId}`, '_blank');
      }
    } catch (error) {
      console.error(error);
      // Show failed snackbar
      toast({
        title: t('createNew.fromBlank.failToast'),
        status: 'error',
      });
    }
  };

  const menu: MenuType = {
    backToMyDesigns: {
      label: t('backToMyDesigns'),
      Icon: BackIcon,
      onClick: onBackToMyDesigns,
    },
    divider01: {},
    createNew: {
      label: t('createNew.label'),
      data: {
        fromTemplate: {
          label: t('createNew.fromTemplate'),
          Icon: TemplatesIcon,
          onClick: onCreateFromTemplate,
        },
        fromBlank: {
          label: t('createNew.fromBlank.label'),
          Icon: BlankTemplateIcon,
          onClick: onCreateFromBlank,
        },
      },
    },
    // Hide below for now
    // showSmartGuides: {
    //   label: 'Show smart guides',
    //   checked: isSmartGuidesOpen,
    //   onClick: setIsSmartGuidesOpen.toggle,
    // },
    // showPrintBleed: {
    //   label: 'Show print bleed',
    //   checked: isShowPrintBleedOpen,
    //   onClick: setIsShowPrintBleedOpen.toggle,
    // },
    makeACopy: {
      label: t('makeACopy.label'),
      onClick: onCopyDesign,
    },
    // saveAsTemplate: {
    //   label: 'Save as temnplate',
    // },
    divider02: {},
    showGrid: {
      label: t('gridLabel'),
      checked: hasShowGrid,
      onClick: onShowGrid,
    },
    showMargin: {
      label: t('marginLabel'),
      checked: hasShowMargin,
      onClick: onShowMargins,
    },
    accessibility: {
      label: t('accessibilityLabel'),
      Icon: AccessibleIcon,
      data: {
        // takeATour: {
        //   label: 'Take a tour',
        // },
        // divider01: {},
        checkAccessibility: {
          Icon: AccessibilityCheckIcon,
          label: t('checkAccessibilityLabel'),
          onClick: () => {
            dispatch(setAccessibilityViewIndex(AccessibilityViewIndex.CHECK));

            Mixpanel.track(ACCESSIBILITY_CHECKER_OPENED, { from: FILE_MENU });
          },
        },
        editReadingOrder: {
          Icon: OrderIcon,
          label: t('readingOrderLabel'),
          onClick: () => {
            dispatch(setAccessibilityViewIndex(AccessibilityViewIndex.EDIT));

            Mixpanel.track(READING_ORDER_TAB_OPENED, { from: FILE_MENU });
          },
        },
        setDocumentLanguage: {
          Icon: LanguageIcon,
          label: t('documentLanguageLabel'),
          onClick: () => {
            onOpen?.();

            Mixpanel.track(DOCUMENT_LANGUAGE_MODAL_OPENED, { from: FILE_MENU });
          },
        },
        visualSimulator: {
          Icon: ColorVisualIcon,
          label: t('visualSimulatorLabel'),
          onClick: () => {
            dispatch(setAccessibilityViewIndex(AccessibilityViewIndex.SIMULATOR));

            Mixpanel.track(VISUAL_SIMULATOR_TAB_OPENED, { from: FILE_MENU });
          },
        },
      },
    },
  };

  return (
    <>
      <ToolbarMenu zIndex='navbar' name={t('label')} menu={menu} ref={fileMenuRef}>
        {t('label')}
      </ToolbarMenu>
      <Portal>
        <SetLanguageModal isOpen={isOpen} onClose={onClose} />
      </Portal>
    </>
  );
});
FileMenu.displayName = 'FileMenu';
